import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PRODUCT_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0d0d0d] border border-white/10 px-4 py-3.5 text-sm text-[#fafafa] placeholder:text-[#666] outline-none transition-colors focus:border-[#e7c15a] focus:ring-1 focus:ring-[#e7c15a]/40";

export default function EnquiryForm({ source = "contact" }) {
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", city: "", product_requirement: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.mobile || !form.email) {
      toast.error("Please fill in your name, mobile and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, source });
      toast.success("Thank you! Your enquiry has been received. We'll reach out shortly.");
      setForm({ full_name: "", mobile: "", email: "", city: "", product_requirement: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="enquiry-name" className={inputCls} placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} />
        <input data-testid="enquiry-mobile" className={inputCls} placeholder="Mobile Number *" value={form.mobile} onChange={set("mobile")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="enquiry-email" type="email" className={inputCls} placeholder="Email Address *" value={form.email} onChange={set("email")} />
        <input data-testid="enquiry-city" className={inputCls} placeholder="City" value={form.city} onChange={set("city")} />
      </div>
      <Select value={form.product_requirement} onValueChange={(v) => setForm((f) => ({ ...f, product_requirement: v }))}>
        <SelectTrigger data-testid="enquiry-product" className="w-full bg-[#0d0d0d] border-white/10 text-sm text-[#fafafa] h-[50px] rounded-none focus:border-[#e7c15a] focus:ring-1 focus:ring-[#e7c15a]/40 data-[placeholder]:text-[#666]">
          <SelectValue placeholder="Product Requirement" />
        </SelectTrigger>
        <SelectContent className="bg-[#111] border-white/10 text-[#fafafa]">
          {PRODUCT_OPTIONS.map((p) => (
            <SelectItem key={p} value={p} className="focus:bg-[#e7c15a]/15 focus:text-[#e7c15a]">{p}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <textarea data-testid="enquiry-message" rows={4} className={inputCls + " resize-none"} placeholder="Your Message" value={form.message} onChange={set("message")} />
      <button
        type="submit"
        disabled={loading}
        data-testid="enquiry-submit"
        className="group relative w-full overflow-hidden bg-[#e7c15a] px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium text-[#050505] disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
        {loading ? "Sending..." : (source === "quote" ? "Request Quote" : "Submit Enquiry")}
      </button>
    </form>
  );
}
